import { AnalyticsConsent } from './AnalyticsConsent'
import { MarketingSEO } from './MarketingSEO'
import { MARKETING_PAGES, marketingSchemas } from './marketingData'
import { useState } from 'react'
import { Link, useRouter } from '../../lib/router'
import { useIsMobile } from '../../lib/useIsMobile'
import { track } from '../../lib/analytics'
import logoWordmark from '../../assets/logo-wordmark.svg'

const NAV_LINKS = [
  { href: '/', label: 'Home' },
  { href: '/workshop-management-software', label: 'Solutions' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/about', label: 'About' },
  { href: '/faq', label: 'FAQ' },
  { href: '/contact', label: 'Contact' },
]

function Logo() {
  const { navigate } = useRouter()
  return (
    <button
      onClick={() => navigate('/')}
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
      aria-label="AutoAxxiz — home"
    >
      <img src={logoWordmark} alt="AutoAxxiz" style={{ height: 34 }} />
    </button>
  )
}

export function MarketingLayout({ children }: { children: React.ReactNode }) {
  const { path, navigate } = useRouter()
  const isMobile = useIsMobile()
  const meta = MARKETING_PAGES[path]
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#fff' }}>

      {meta && <MarketingSEO title={meta.title} description={meta.description} path={path} jsonLd={marketingSchemas(path)} />}
      <AnalyticsConsent />
      {/* ── Nav ─────────────────────────────────────────────────── */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(8px)',
        borderBottom: '1px solid var(--border-subtle)',
      }}>
        <div style={{
          maxWidth: 1120, margin: '0 auto', padding: '0 var(--space-6)',
          height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Logo />

          {/* Desktop nav */}
          {!isMobile && (
            <nav aria-label="Main navigation" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              {NAV_LINKS.map(l => (
                <Link
                  key={l.href} href={l.href}
                  style={{
                    padding: '6px 12px', borderRadius: 6, fontSize: 14, fontWeight: 500,
                    color: path === l.href ? 'var(--navy-700)' : 'var(--fg-3)',
                    background: path === l.href ? 'var(--navy-50)' : 'transparent',
                    textDecoration: 'none',
                  }}
                >{l.label}</Link>
              ))}
            </nav>
          )}

          {/* Desktop CTAs */}
          {!isMobile && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                onClick={() => navigate('/login')}
                style={{
                  padding: '8px 16px', borderRadius: 8, fontSize: 14, fontWeight: 500,
                  background: 'transparent', border: '1px solid var(--border-strong)',
                  color: 'var(--fg-2)', cursor: 'pointer',
                }}
              >Log in</button>
              <button
                onClick={() => { track('Marketing CTA Click', { location: 'nav', cta: 'start_free_trial' }); navigate('/signup') }}
                style={{
                  padding: '8px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  background: 'var(--accent)', border: 'none',
                  color: '#fff', cursor: 'pointer',
                }}
              >Start free trial</button>
            </div>
          )}

          {/* Mobile: Log in + hamburger */}
          {isMobile && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                onClick={() => navigate('/login')}
                style={{
                  padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                  background: 'transparent', border: '1px solid var(--border-strong)',
                  color: 'var(--fg-2)', cursor: 'pointer',
                }}
              >Log in</button>
              <button
                onClick={() => setMobileOpen(o => !o)}
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: 6, borderRadius: 6, color: 'var(--fg-2)', display: 'flex',
                }}
              >
                {mobileOpen ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Mobile drawer menu */}
        {isMobile && mobileOpen && (
          <div style={{
            borderTop: '1px solid var(--border-subtle)',
            background: '#fff', padding: '12px 0 20px',
          }}>
            {NAV_LINKS.map(l => (
              <Link
                key={l.href} href={l.href}
                onClick={() => setMobileOpen(false)}
                style={{
                  display: 'block', padding: '12px 24px', fontSize: 15, fontWeight: 500,
                  color: path === l.href ? 'var(--navy-700)' : 'var(--fg-2)',
                  background: path === l.href ? 'var(--navy-50)' : 'transparent',
                  textDecoration: 'none',
                }}
              >{l.label}</Link>
            ))}
            <div style={{ padding: '16px 24px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={() => { setMobileOpen(false); track('Marketing CTA Click', { location: 'mobile_nav', cta: 'start_free_trial' }); navigate('/signup') }}
                style={{
                  padding: '12px 16px', borderRadius: 8, fontSize: 15, fontWeight: 600,
                  background: 'var(--accent)', border: 'none', color: '#fff', cursor: 'pointer',
                }}
              >Start free trial</button>
            </div>
          </div>
        )}
      </header>

      {/* ── Content ─────────────────────────────────────────────── */}
      <main style={{ flex: 1 }}>{children}</main>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer style={{
        background: 'var(--navy-900)', color: 'var(--neutral-400)',
        padding: 'var(--space-16) var(--space-6) var(--space-10)',
      }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1fr 1fr 1fr',
            gap: isMobile ? 'var(--space-8)' : 'var(--space-10)',
            marginBottom: 'var(--space-12)',
          }}>
            {/* Brand column — full width on mobile */}
            <div style={{ gridColumn: isMobile ? '1 / -1' : undefined }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 44" width="36" height="36" fill="none">
                  <rect width="44" height="44" rx="10" fill="#243d5c" />
                  <path d="M10 34 L22 10 L34 34" stroke="#FFFFFF" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M14 26 L30 26" stroke="#D96E1A" strokeWidth="3.5" strokeLinecap="round" />
                </svg>
                <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, color: '#fff', letterSpacing: '-0.02em' }}>
                  Auto<span style={{ color: 'var(--orange-500, #D96E1A)' }}>Axxiz</span>
                </span>
              </div>
              <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--neutral-400)', maxWidth: 280, marginBottom: 16 }}>
                The modern, WhatsApp-native workshop management system built for South African independent workshops. Not translated from America.
              </p>
              <p style={{ fontSize: 12, color: 'var(--neutral-600)' }}>
                AutoAxxiz is a product by JVS Ethics Lab<br />
                Johannesburg, Gauteng, South Africa<br />
                Billing and tax invoices are issued by JVS Ethics Lab
              </p>
            </div>

            {/* Product */}
            <div>
              <h3 style={{ color: 'var(--neutral-300)', marginBottom: 16, fontSize: 12 }}>Product</h3>
              {[{ href: '/pricing', label: 'Pricing' }, { href: '/#features', label: 'Features' }, { href: '/#how-it-works', label: 'How it works' }, { href: '/contact', label: 'Request demo' }, { href: '/digital-job-cards', label: 'Digital job card guide' }, { href: '/workshop-load-shedding', label: 'Load-shedding guide' }].map(l => (
                <div key={l.href} style={{ marginBottom: 8 }}>
                  <Link href={l.href} style={{ color: 'var(--neutral-400)', fontSize: 14, textDecoration: 'none' }}>{l.label}</Link>
                </div>
              ))}
              {[{ href: '/whatsapp-workshop-management', label: 'WhatsApp workflow' }, { href: '/digital-vehicle-inspection-software', label: 'Digital inspections' }, { href: '/workshop-payment-links', label: 'Payment links' }, { href: '/workshop-accounting-sync', label: 'Accounting sync' }].map(l => (
                <div key={l.href} style={{ marginBottom: 8 }}>
                  <Link href={l.href} style={{ color: 'var(--neutral-400)', fontSize: 14, textDecoration: 'none' }}>{l.label}</Link>
                </div>
              ))}
            </div>

            {/* Company */}
            <div>
              <h3 style={{ color: 'var(--neutral-300)', marginBottom: 16, fontSize: 12 }}>Company</h3>
              {[{ href: '/about', label: 'About us' }, { href: '/contact', label: 'Contact' }, { href: '/faq', label: 'FAQ' }].map(l => (
                <div key={l.href} style={{ marginBottom: 8 }}>
                  <Link href={l.href} style={{ color: 'var(--neutral-400)', fontSize: 14, textDecoration: 'none' }}>{l.label}</Link>
                </div>
              ))}
            </div>

            {/* Legal */}
            <div>
              <h3 style={{ color: 'var(--neutral-300)', marginBottom: 16, fontSize: 12 }}>Legal</h3>
              {[{ href: '/terms', label: 'Terms of service' }, { href: '/privacy', label: 'Privacy policy' }].map(l => (
                <div key={l.href} style={{ marginBottom: 8 }}>
                  <Link href={l.href} style={{ color: 'var(--neutral-400)', fontSize: 14, textDecoration: 'none' }}>{l.label}</Link>
                </div>
              ))}
              <button onClick={() => window.dispatchEvent(new Event('axxiz-analytics-settings'))} style={{ background: 'transparent', border: 'none', padding: 0, color: 'var(--neutral-400)', cursor: 'pointer', fontSize: 14 }}>Analytics preferences</button>
              <div style={{ marginTop: 20 }}>
                <p style={{ fontSize: 12, color: 'var(--neutral-600)', lineHeight: 1.6 }}>
                  Billing by JVS Ethics Lab<br />
                  SARS-compliant Tax Invoices<br />
                  POPIA Information Officer on request
                </p>
              </div>
            </div>
          </div>

          <div style={{
            borderTop: '1px solid var(--navy-800)', paddingTop: 'var(--space-6)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexWrap: 'wrap', gap: 12,
          }}>
            <p style={{ fontSize: 12, color: 'var(--neutral-600)' }}>© 2026 JVS Ethics Lab. AutoAxxiz is a product by JVS Ethics Lab.</p>
            <p style={{ fontSize: 12, color: 'var(--neutral-600)' }}>Built in Johannesburg · Billed in rand · Supported by a human in Joburg</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
